import {getCurrentUserLogin, isLoggedIn} from '@github-ui/client-env'
import type {RepositoryDataResponse} from '@github-ui/data-router-error-helpers'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {sendEvent} from '@github-ui/hydro-analytics'
import {createQueryClient, getQueryClient} from '@github-ui/query-client'
import {DataRouterApplicationBuilder} from '@github-ui/react-core/future/data-router-application'
import {mainQuery, makeSelectDataFromQueryData} from '@github-ui/react-core/future/main-query'
import {queryFnFetch} from '@github-ui/react-core/future/query-fn-fetch'
import type {RouteQueryKey} from '@github-ui/react-core/query-key'
import {
  createServerPersistedQueryPersister,
  persistQueryData,
  removePersistedQueryData,
} from '@github-ui/react-query-persister'
import {matchPath} from '@github-ui/react-router'
import {IssueViewerPage} from '@github-ui/server-persisted-queries'
import type {IssueViewerPageQuery} from '@github-ui/server-persisted-queries/types/IssueViewerPage'
import {IS_SERVER} from '@github-ui/ssr-utils'
import {
  getIssueViewerFeed,
  getIssueViewerStorageQueryKey,
  type IssueViewerActivityTimeline,
  type IssueViewerFeed,
  ISSUE_VIEWER_CACHE_TTL_MS,
  ISSUE_VIEWER_PERSISTER_PREFIX,
  rememberIssueViewerActivityTimelineState,
} from './issue-viewer-storage-key'

import {fetchAndWarmPreheat, runSharedPreheat} from './shared-preheat-run'
import {initIssueViewerHPC, observeIssueViewerFirstData} from './issue-viewer-hpc'

export type IssueViewerPayload = RepositoryDataResponse<IssueViewerPageQuery> & {
  feed: IssueViewerFeed
  /** Whether the viewer can turn the Activity timeline preview off or back on. Older cached payloads lack it. */
  activityTimeline?: IssueViewerActivityTimeline
  /** Positive crawler page selected into `timelineItems`, when the backend supports page-aware payloads. */
  timelinePage?: number
}

export type IssueViewerQueryData = {
  meta?: {title?: string}
  payload: IssueViewerPayload
}

/**
 * Thrown when a transient GraphQL execution failure leaves the issue viewer payload without
 * repository data. Reported as a failed refresh rather than a route render failure.
 */
export class IssueViewerRefreshError extends Error {
  constructor(errors: unknown) {
    super(`Issue viewer refresh failed: ${JSON.stringify(errors)}`)
    this.name = 'IssueViewerRefreshError'
  }
}

export type IssueCoordinates = {owner: string; repo: string; number: number; allowedOwner?: string}
export type IssueViewerQueryIdentifier = IssueCoordinates
export type IssueViewerDocumentMode = 'canonical' | 'highlighted-event' | 'timeline-page'

const preheatTimestamps = new WeakMap<object, number>()
let preheatQueryClient: ReturnType<typeof createQueryClient> | undefined

export function buildIssueViewerRoute(appBuilder: DataRouterApplicationBuilder<string>) {
  initIssueViewerHPC()
  const query = mainQuery<IssueViewerPayload>({
    queryDeps: ({pathname, searchParams, hash}) => {
      const timelinePage = parseIssueViewerTimelinePage(searchParams)
      return {
        pathname,
        searchParams: {
          queryId: IssueViewerPage,
          ...(timelinePage ? {timeline_page: String(timelinePage)} : {}),
        },
        documentMode: issueViewerDocumentMode({searchParams, hash: hash ?? ''}),
      }
    },
    persister: createServerPersistedQueryPersister({
      queryHash: IssueViewerPage,
      storageQueryKey: (queryKey, queryData) => {
        const queryDeps = queryKey[4]
        if (!isRecord(queryDeps) || typeof queryDeps.pathname !== 'string') return

        const match = matchPath('/:owner/:repo/issues/:number', queryDeps.pathname)
        const {owner, repo, number} = match?.params ?? {}
        if (!owner || !repo || !number || !/^\d+$/.test(number)) return

        const issueNumber = Number(number)
        if (!Number.isSafeInteger(issueNumber) || issueNumber <= 0) return

        try {
          const feed =
            isIssueViewerQueryData(queryData) && isIssueViewerFeed(queryData.payload.feed)
              ? queryData.payload.feed
              : getIssueViewerFeed()
          return getIssueViewerStorageQueryKey({
            number: issueNumber,
            owner: decodeURIComponent(owner),
            repo: decodeURIComponent(repo),
            feed,
          })
        } catch {
          return
        }
      },
      maxAgeMs: ISSUE_VIEWER_CACHE_TTL_MS,
      prefix: ISSUE_VIEWER_PERSISTER_PREFIX,
      // An IndexedDB snapshot may be stale even within its TTL, so revalidate after restoring it.
      alwaysRefetch: true,
      restoreOnPreload: true,
      // SSR uses setQueryData rather than a fetch; explicitly persist that initial response.
      persistInitialData: true,
      getDataUpdatedAt: data => (isRecord(data) ? preheatTimestamps.get(data) : undefined),
      dataValidator: {name: 'issue-viewer-page', isValid: isCacheableIssueViewerQueryData},
      getPersistenceMode: persistedQuery =>
        issueViewerDocumentModeFromQueryKey(persistedQuery.queryKey) === 'canonical' ? 'read-write' : 'disabled',
      sendAnalyticsEvent: (eventType, target, payload) => sendEvent(eventType, {...payload, target}),
    }),
  })

  return appBuilder.createQueryRouteConfig('issueViewerRoute', {
    path: '/:owner/:repo/issues/:number',
    queries: [
      {
        ...query,
        observeFirstData: observeIssueViewerFirstData,
        select: makeSelectDataFromQueryData<IssueViewerPayload>(),
        // eslint-disable-next-line @github-ui/github-monorepo/require-query-schema
        queryFn: async (
          queryKey: Parameters<typeof query.queryFn>[0],
          options: Parameters<typeof query.queryFn>[1],
        ) => {
          const allowedOwner = typeof options.meta?.allowedOwner === 'string' ? options.meta.allowedOwner : undefined
          const routeSearchParams = queryKey.queryDeps.searchParams
          if (!isStringRecord(routeSearchParams)) throw new Error('Invalid issue viewer query search params')
          const queryDeps = {
            ...queryKey.queryDeps,
            searchParams: {
              ...routeSearchParams,
              queryId: IssueViewerPage,
              ...(allowedOwner ? {allowedOwner} : {}),
            },
          }
          const login = getCurrentUserLogin()
          const queryData =
            options.meta?.isPreload === true
              ? await fetchPrimaryPreheat(queryDeps.pathname, options, allowedOwner)
              : await query.queryFn({...queryKey, queryDeps}, options)
          return finalizeIssueViewerQueryData(queryData, login)
        },
      },
    ],
  })
}

export function finalizeIssueViewerQueryData<T>(queryData: T, login: ReturnType<typeof getCurrentUserLogin>): T {
  const result = assertRepositoryDataUsable(queryData)
  // Restored snapshots can predate the viewer's latest choice, so only fresh responses for the
  // account that requested them count.
  if (!IS_SERVER && isIssueViewerQueryData(result) && getCurrentUserLogin() === login) {
    rememberIssueViewerActivityTimelineState(result.payload.activityTimeline?.state)
  }
  return result
}

let preheatRoute: ReturnType<typeof buildIssueViewerRoute> | undefined

function getPreheatRoute() {
  return (preheatRoute ??= buildIssueViewerRoute(
    // eslint-disable-next-line @github-ui/github-monorepo/react-app-name -- Builds query options for the existing issues app; does not register an app.
    DataRouterApplicationBuilder.create('issues', {
      queryKeyAppName: () => 'repo',
    }),
  ))
}

export function issueViewerPrimaryQueryKey(identifier: IssueViewerQueryIdentifier) {
  const route = getPreheatRoute()
  const pathname = route.generatePath({
    owner: encodeURIComponent(identifier.owner),
    repo: encodeURIComponent(identifier.repo),
    number: String(identifier.number),
  })
  return route.queries.mainQuery.makeQueryKey({
    pathname,
    searchParams: {
      queryId: IssueViewerPage,
    },
    documentMode: 'canonical',
  })
}

export function issueViewerPrimaryQueryOptions(identifier: IssueViewerQueryIdentifier) {
  const route = getPreheatRoute()
  const options = route.buildQueryOptions('mainQuery', issueViewerPrimaryQueryKey(identifier))
  return {
    ...options,
    // The owner affects request revalidation, not the shared issue cache identity.
    meta: {...options.meta, allowedOwner: identifier.allowedOwner},
  }
}

export function issueViewerDocumentMode({
  searchParams,
  hash,
}: {
  searchParams: URLSearchParams
  hash: string
}): IssueViewerDocumentMode {
  if (parseIssueViewerTimelinePage(searchParams)) return 'timeline-page'
  if (/^#(?:issuecomment|event)-[^-]+$/.test(hash)) return 'highlighted-event'
  return 'canonical'
}

export function parseIssueViewerTimelinePage(searchParams: URLSearchParams): number | undefined {
  const timelinePage = Number(searchParams.get('timeline_page'))
  return Number.isSafeInteger(timelinePage) && timelinePage > 0 ? timelinePage : undefined
}

export function isCanonicalIssueViewerQueryKey(queryKey: readonly unknown[]): boolean {
  return issueViewerDocumentModeFromQueryKey(queryKey) === 'canonical'
}

export async function persistIssueViewerPrimary(
  identifier: IssueViewerQueryIdentifier,
  queryClient: ReturnType<typeof getQueryClient> = getQueryClient(),
): Promise<void> {
  const options = issueViewerPrimaryQueryOptions(identifier)
  await persistQueryData(options.persister, options.queryKey, queryClient)
}

export async function removePersistedIssueViewerPrimary(
  identifier: IssueViewerQueryIdentifier,
  queryClient: ReturnType<typeof getQueryClient> = getQueryClient(),
): Promise<void> {
  const options = issueViewerPrimaryQueryOptions(identifier)
  await removePersistedQueryData(options.persister, options.queryKey, queryClient)
}

export async function preheatIssueViewerPrimary({
  owner,
  repo,
  number,
  allowedOwner,
  source,
  signal,
  onError,
}: IssueCoordinates & {source: string; signal?: AbortSignal; onError?: (error: unknown) => void}): Promise<void> {
  if (!canPreheat() || signal?.aborted || !Number.isSafeInteger(number) || number <= 0) return

  const login = getCurrentUserLogin()
  const queryClient = getQueryClient()
  const queryOptions = issueViewerPrimaryQueryOptions({owner, repo, number, allowedOwner})
  const queryKey: RouteQueryKey = queryOptions.queryKey
  const existingQuery = queryClient.getQueryCache().find({queryKey, exact: true})
  if (existingQuery && (existingQuery.getObserversCount() > 0 || existingQuery.state.fetchStatus !== 'idle')) return
  const state = existingQuery?.state
  if (state?.status === 'success' && !state.isInvalidated && isCacheableIssueViewerQueryData(state.data)) return

  // Keep pending preheats private so navigation can fetch independently.
  const preheatClient = (preheatQueryClient ??= createQueryClient())
  const meta = {...queryOptions.meta, isPreload: true, preloadedAt: Date.now(), preheatSource: source}
  await runSharedPreheat({
    client: preheatClient,
    queryKey,
    signal,
    onError,
    fetch: () =>
      fetchAndWarmPreheat({
        client: preheatClient,
        sharedClient: queryClient,
        queryOptions,
        fetch: () => preheatClient.fetchQuery({...queryOptions, gcTime: 0, meta}),
        canPublish: () => canPreheat() && getCurrentUserLogin() === login,
      }),
  })
}

async function fetchPrimaryPreheat(
  pathname: string,
  options: {signal: AbortSignal; meta: Record<string, unknown> | undefined},
  allowedOwner?: string,
): Promise<IssueViewerQueryData> {
  if (!canPreheat()) throw new Error('Issue preheating is disabled')

  const match = matchPath('/:owner/:repo/issues/:number', pathname)
  const {owner: encodedOwner, repo: encodedRepo, number} = match?.params ?? {}
  const owner = encodedOwner && decodeURIComponent(encodedOwner)
  const repo = encodedRepo && decodeURIComponent(encodedRepo)
  const issueNumber = Number(number)
  if (!owner || !repo || !number || !/^\d+$/.test(number) || !Number.isSafeInteger(issueNumber) || issueNumber <= 0) {
    throw new Error('Invalid issue preheat coordinates')
  }

  const source = typeof options.meta?.preheatSource === 'string' ? options.meta.preheatSource : 'data-router'
  const response = await queryFnFetch<unknown>({
    queryDeps: {
      pathname: `/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/issues/preheat`,
      searchParams: {
        number,
        source,
        query: 'IssueViewerPage',
        version: '1',
        queryId: IssueViewerPage,
        ...(allowedOwner ? {allowedOwner} : {}),
      },
      init: {signal: options.signal, headers: {'X-GITHUB-PREHEAT': source}},
    },
  })

  if (
    !isRecord(response) ||
    response.version !== 1 ||
    response.queryHash !== IssueViewerPage ||
    typeof response.dataUpdatedAt !== 'number' ||
    !Number.isSafeInteger(response.dataUpdatedAt) ||
    response.dataUpdatedAt <= 0 ||
    response.dataUpdatedAt > Date.now() + 60_000 ||
    Date.now() - response.dataUpdatedAt > ISSUE_VIEWER_CACHE_TTL_MS ||
    !isCacheableIssueViewerQueryData(response.queryData)
  ) {
    throw new Error('Unsupported issue preheat response')
  }

  const issue = response.queryData.payload.data?.repository?.issue
  if (
    !issue ||
    issue.number !== issueNumber ||
    issue.repository.owner.login.toLowerCase() !== owner.toLowerCase() ||
    issue.repository.name.toLowerCase() !== repo.toLowerCase()
  ) {
    throw new Error('Issue preheat response does not match the requested issue')
  }

  preheatTimestamps.set(response.queryData, response.dataUpdatedAt)
  return response.queryData
}

function canPreheat(): boolean {
  return !IS_SERVER && isLoggedIn() && !isFeatureEnabled('disable_preheating_for_issues')
}

function issueViewerDocumentModeFromQueryKey(queryKey: readonly unknown[]): IssueViewerDocumentMode {
  const queryDeps = queryKey[4]
  if (!isRecord(queryDeps)) return 'canonical'
  const documentMode = queryDeps.documentMode
  return documentMode === 'highlighted-event' || documentMode === 'timeline-page' ? documentMode : 'canonical'
}

/**
 * An HTTP 200 GraphQL envelope that carries only untyped execution errors instead of
 * `data.repository` is a transient backend failure, not a result. Throwing keeps TanStack Query
 * from replacing previously loaded issue data with it, so the route can keep rendering what it
 * already has. Typed errors (SAML, FORBIDDEN, NOT_FOUND) still resolve, so the existing reload and
 * error-boundary handling in `handleRepositoryDataError` runs unchanged.
 */
function assertRepositoryDataUsable<T>(queryData: T): T {
  const payload = isRecord(queryData) ? queryData.payload : undefined
  if (!isRecord(payload)) return queryData

  const data = payload.data
  if (isRecord(data) && data.repository) return queryData

  const errors = payload.errors
  if (!Array.isArray(errors) || errors.length === 0) return queryData
  if (errors.some(error => !isRecord(error) || typeof error.type === 'string')) return queryData

  throw new IssueViewerRefreshError(errors)
}

function isCacheableIssueViewerQueryData(value: unknown): value is IssueViewerQueryData {
  if (!isRecord(value) || !isRecord(value.payload)) return false

  const {meta, payload} = value
  if (meta !== undefined && (!isRecord(meta) || (meta.title !== undefined && typeof meta.title !== 'string'))) {
    return false
  }
  if (payload.errors != null && (!Array.isArray(payload.errors) || payload.errors.length > 0)) return false
  if (!isIssueViewerFeed(payload.feed)) return false

  const data = payload.data
  if (!isRecord(data) || !isRecord(data.repository) || !isRecord(data.repository.issue)) return false

  return typeof data.repository.issue.id === 'string' && data.repository.issue.id.length > 0
}

function isIssueViewerQueryData(value: unknown): value is IssueViewerQueryData {
  return isRecord(value) && isRecord(value.payload)
}

function isIssueViewerFeed(value: unknown): value is IssueViewerFeed {
  return value === 'comments' || value === 'activity'
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isStringRecord(value: unknown): value is Record<string, string> {
  return isRecord(value) && Object.values(value).every(item => typeof item === 'string')
}
